import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { MAIN_CONTENT_ID } from "@/components/Layout/BaseLayouts/ContentContainer";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

/**
 * First Tab stop: jumps past chrome into `<main id="main-content">`.
 * Visually hidden until focused.
 *
 * Under HashRouter a bare `href="#main-content"` is treated as the route
 * `/main-content` and lands on the 404 page — so navigation is prevented and
 * focus is moved in JS instead. The href stays for semantics.
 */
export const SkipToContent = () => {
  const { t } = useTranslation();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const main = document.getElementById(MAIN_CONTENT_ID);
    if (!main) {
      return;
    }

    main.focus();
    // jsdom (unit tests) has no scrollIntoView — guard for that.
    main.scrollIntoView?.({ block: "start" });
  };

  return (
    <a
      className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-surface-primary focus:px-4 focus:py-2 focus:text-body-base focus:font-semibold focus:text-foreground-primary-inverse focus:outline-none focus:ring-2 focus:ring-border-primary-inverse focus:ring-offset-2 focus:ring-offset-surface-primary"
      href={`#${MAIN_CONTENT_ID}`}
      tabIndex={KEYBOARD_TAB_INDEX}
      onClick={handleClick}
    >
      {t("layout.skipToContent")}
    </a>
  );
};
